<template>
  <div class="content-block">
    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid" class="dx-card wide-card grid-tramites">
      <DxColumn data-field="IdBorrador" :visible="false"></DxColumn>
      <DxColumn data-field="CodigoSecuencial" :caption="$t('tramites.grid.id')" format="0000000000"></DxColumn>
      <DxColumn data-field="FechaAlta" :caption="$t('tramites.grid.fecha_alta')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="NombreDelegacionInmueble" :caption="$t('tramites.grid.delegacion')"></DxColumn>
      <DxColumn data-field="NombreAgente" :caption="$t('tramites.grid.agente')"></DxColumn>
      <DxColumn data-field="TipoSuministro" :caption="$t('tramites.grid.tipo_suministro')" cell-template="serviciosTemplate"></DxColumn>
      <DxColumn data-field="DireccionInmueble" :caption="$t('tramites.grid.direccion_inmueble')"></DxColumn>
      <DxColumn data-field="DocumentoTitular" :caption="$t('tramites.grid.documento_titular')"></DxColumn>
      <DxColumn data-field="CodigoEstadoCalculado" :caption="$t('tramites.grid.estado')" cell-template="estadoTemplate" lookup-enabled="true">
        <DxLookup v-bind="getLookup('operaciones', 'estado_calculado')"></DxLookup>
      </DxColumn>
      <DxColumn v-if="puedoVerCoste" data-field="Comision" :caption="$t('tramites.grid.comision')" data-type="number">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn v-if="puedoVerCoste" data-field="Retrocomision" :caption="$t('tramites.grid.retrocomision')" data-type="number"
        :calculate-cell-value="formatRetrocomision" :calculate-filter-expression="getRetrocomisionFilter">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn data-field="FechaModificacion" :caption="$t('tramites.grid.fecha_ultima_modificacion')" data-type="date"
        format="dd/MM/yyyy"></DxColumn>
      <DxColumn type="buttons" :caption="$t('tramites.grid.acciones')" :fixed="true" width="100px"
        fixed-position="right" :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-comment-dots text-success',
            hint: $t('tramites.grid.ir_a_chat'),
            onClick: (e: any) => irAChat(e),
            visible: (options: any) => options.row?.data.NombreResponsable !== null
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-solid fa-magnifying-glass text-success',
            hint: $t('tramites.grid.ver_detalle'),
            onClick: (e: any) => verDetalle(e.row.data)
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-trash text-success',
            hint: $t('tramites.grid.borrar'),
            onClick: (e: any) => borrar(e.row.data)
          }
        ]"/>
      <DxColumnFixing :enabled="true" />

      <template #estadoTemplate="{ data }">
        <div :class="[
          `d-inline-block rounded py-1 px-2`,
          `estado-operacion ${data.value.replace(' ', '')}`,
        ]">
            {{ $t(`operaciones.estado_calculado.${data.value}`) }}
        </div>
      </template>
      <template #serviciosTemplate="{ data }">
          <div :class="[`d-inline-block rounded py-1 px-2 me-2`, `servicio ${data.value.replace(' ', '')}`]">
            <i :class="[
              'me-1',
              data.value === 'AGUA' ? 'fa-regular fa-droplet' :
                data.value === 'LUZ' ? 'fa-regular fa-lightbulb' :
                  'fa-regular fa-fire'
            ]"></i>
            {{ $t(`inmuebles.servicios.${data.value}`) }}
          </div>
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { useApi } from "@api/integration";
import {
  DxColumn,
  DxDataGrid,
  DxFormat,
  DxLookup,
  DxColumnFixing,
} from "devextreme-vue/data-grid";
import {
  currencyFormat,
  getDefaultGridProperties,
  getLookup,
  getRetrocomisionFilter
} from "@helpers/grid-helper";
import { useModalService } from "@services/modal-service";
import type CustomStore from "devextreme/data/custom_store";
import { onMounted, ref } from "vue";
import { useIntl } from "@/.shared/services/i18n/i18nservice";
import { useAuthorization } from "@/.shared/services/auth/authorization";
import { PermissionsApp } from "@/.shared/services/auth/authorization/authorization-service-impl";
import type { DXLoadOptions } from "@/.shared/.api";
import { mostrarDetalleBorrador, mostrarDetalleOperacion } from "@/services/OperacionDetalleModalFactory";
import type DevExpress from "devextreme";
import { messageBoxResult } from "@/.shared/components/dialogs/message-box";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import { ApiError } from "@/.shared/services/http";
import { DRAFT_OPERATION_ALREADY_IN_COURSE } from "@/.shared/errors/codes";

const api = useApi();
const gridSource = ref<CustomStore>();
const grid = ref<DxDataGrid>();
const modal = useModalService();
const intl = useIntl();
const autho = useAuthorization();

onMounted(async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;
      return await api.callStandardApi("getTramitesForGridColaboradores", { showLoading: false }, undefined, q);
    }
  });

});

const verDetalle = async (data: any) => {
  const idEntidad = data.IdOperacion ?? data.IdBorrador;
  const tipoEntidad = data.IdOperacion ? 'operacion' : 'borrador';
  let tipo = data.TipoSuministro;

  switch (tipoEntidad) {
    case 'operacion':
      if (tipo === 'LUZ' || tipo === 'GAS') {
        tipo = 'ENERGIA';
      }
      await mostrarDetalleOperacion(idEntidad, tipo);
      break;
    case 'borrador':
      await mostrarDetalleBorrador(idEntidad);
      break;
  }
}

async function irAChat(data: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent) {
  let rutaColaboradores = import.meta.env.VITE_BASE_URL;
  let url = `${rutaColaboradores}/comunicaciones-operacion/${data.row?.data.IdOperacion}/${data.row?.data.CodigoSecuencial.toString().padStart(10, '0')}/${data.row?.data.TipoSuministro}`;
  window.open(url, '_blank');
}

const puedoVerCoste = autho.hasPermission(PermissionsApp.EditSelfEmpresaInmobiliaria) || autho.hasPermission(PermissionsApp.EditSameCompanyDelegacion);

const formatRetrocomision = (rowData: any) => {
  return rowData.Retrocomision ? -Math.abs(rowData.Retrocomision) : rowData.Retrocomision;
};

const borrar = async (data: any) => {
  let respuesta = await modal.messageBox(intl.i18n.t('tramites.grid.borrar_pregunta'), intl.i18n.t('tramites.grid.titulo_confirma_borrar'), "okCancel", "warning");
  if (respuesta == messageBoxResult.ok)
    try {
      await api.callStandardApi("deleteBorradorOperacion", { showLoading: true }, data.IdBorrador)
        .then(() => {
          toastSuccess(intl.i18n.t('tramites.borrado_exito'));
          grid.value?.instance?.refresh();
        })
    } catch (e: any) {
      if (e instanceof ApiError && e.details?.errorList.length === 1 && [DRAFT_OPERATION_ALREADY_IN_COURSE].includes(e.details.errorList[0].Code)) {
        toastError(intl.i18n.t(`shared.errors.api.${e.details.errorList[0].Code}`));
      } else {
        toastError(intl.i18n.t('tramites.borrado_fallo'));
      }
    }
}

</script>

<style lang="scss" scoped>




.detalle {
  color: var(--text-primary-default);
  cursor: pointer;
}
</style>
<style>
.grid-tramites .dx-datagrid {
  max-height: calc(100vh - 250px);
}
</style>
